// React and routing
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

// Context and hooks
import { useAuth } from "../../Context/AuthContext";
import { useUser } from "../../Context/UserContext";
import useAPI from "../../Hooks/useAPI";

// Components
import { default as Footer } from "../../Components/Footer/FooterSmall";
import Header2 from "../../Components/Header2/Header2";
import Input from "../../Components/Input/Input";
import Button from "../../Components/Button/Button";
import Button2 from "../../Components/Button2/Button2";

// Styles
import "../../Components/Page/Page.css";
import "./Login.css";

//images
import logo from "../../Images/Logo/White Background .png";
import text from "../../Images/Logo/IceGemsText.png";
import Rotator from "../../Components/Rotator/Rotator";
import {
  useQueryParams,
  useQueryParamsActions,
} from "../../Context/QueryParamsProvider";
import { EncryptionService } from "../../services/EncryptionService";
import { Role } from "../../Context/UserContext";
import { useModal } from "../../modal/modalContext";
// Interfaces
interface LoginFormData {
  email: string;
  password: string;
}

//Login page
export default function Login() {
  const navigate = useNavigate();
  const queryParams = useQueryParams();
  const { clearQueryParams } = useQueryParamsActions();
  const { redirect, docCode } = queryParams;
  const { login } = useAuth();
  const { setUser: setUserContext } = useUser();
  const { login: apiLogin, getMyPermissions, getMyRoles } = useAPI();
  const [formData, setFormData] = useState<LoginFormData>({
    email: "",
    password: "",
  });
  const [error, setError] = useState<string>("");
  const [encryptionService] = useState(() => new EncryptionService());
  const { openModal } = useModal();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      const data = await apiLogin(formData.email, formData.password);

      // Store token first
      login(data.token, data.user);

      // Then get user permissions and roles
      const [permissions, roles] = await Promise.all([
        getMyPermissions(),
        getMyRoles(),
      ]);

      // Create user object with all data
      const userWithData = {
        ...data.user,
        permissions: permissions || [],
        roles: roles || [],
      };

      // Update user context with complete user data
      setUserContext(userWithData);
      // Check if redirect parameter is present

      // Load public key for encryption
      if (redirect) {
        let bContractAdminstrator = roles.some(
          (role: Role) => role.name === "contract-administrator",
        );
        try {
          // Load public key for encryption
          await encryptionService.loadPublicKey();
          // Encrypt the parameters
          const encryptedUserId = encryptionService.encryptField(data.user.id);
          const encryptedDocCode = docCode
            ? encryptionService.encryptField(docCode)
            : "";
          const encryptedEntityID = encryptionService.encryptField(
            data.user.entityid || "1",
          );
          const encryptedRole = encryptionService.encryptField(
            bContractAdminstrator.toString(),
          );
          const encryptedFirstName = encryptionService.encryptField(
            data.user.firstName,
          );
          const encryptedLastName = encryptionService.encryptField(
            data.user.lastName,
          );
          const encryptedEmail = encryptionService.encryptField(
            data.user.email,
          );
          const encryptedAddress = encryptionService.encryptField(
            data.user.streetaddress || "",
          );
          const encryptedAddress2 = encryptionService.encryptField(
            data.user.streetaddress2 || "",
          );
          const encryptedCity = encryptionService.encryptField(
            data.user.city || "",
          );
          const encryptedState = encryptionService.encryptField(
            data.user.state || "",
          );
          const encryptedZip = encryptionService.encryptField(
            data.user.zip || "",
          );

          // Create redirect URL with encrypted parameters, to send to the contracts website
          console.log(redirect);
          const separator = redirect.includes("?") ? "&" : "?";
          const redirectUrl = `${redirect}${separator}g=${encodeURIComponent(
            encryptedUserId,
          )}${
            docCode ? `&h=${encodeURIComponent(encryptedDocCode)}` : ""
          }&i=${encodeURIComponent(encryptedEntityID)}&j=${encodeURIComponent(
            encryptedRole,
          )}&e=${encodeURIComponent(encryptedEmail)}&b=${encodeURIComponent(
            encryptedFirstName,
          )}&c=${encodeURIComponent(encryptedLastName)}&k=${encodeURIComponent(
            encryptedAddress,
          )}&l=${encodeURIComponent(encryptedCity)}&m=${encodeURIComponent(
            encryptedState,
          )}&n=${encodeURIComponent(encryptedZip)}&o=${encodeURIComponent(
            encryptedAddress2,
          )}`;
          console.log(redirectUrl);
          // Clear query parameters from context before redirecting
          clearQueryParams();
          // redirect to the contracts website
          window.location.href = redirectUrl;
        } catch (encryptionError) {
          console.error("Encryption failed:", encryptionError);
          setError("Failed to encrypt redirect parameters");
        }
        navigate("/profile");
      } else {
        if (data.user.messagecount > 0) {
          const userid = data.user.id;
          openModal("notification", userid ? { userid } : { userid: "" });
          navigate("/profile");
        } else {
          // Navigate to home page
          navigate("/profile");
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  return (
    <div className="generalPage">
      <Header2 />
      <Rotator />

      <div className="pageContainer">
        <div className="login-container">
          <div className="login-card">
            {/* HEADER */}
            <div className="login-header">
              <img src={logo} className="logoImage" alt="logo" />
              {/* <img src={text} className="logoText" alt="text" /> */}
            </div>

            <h2 className="login-title">Welcome to Ice Gems</h2>
            <p className="login-subtitle">Sign in to your account</p>

            {error && (
              <div id="error-message" className="error-message">
                {error}
              </div>
            )}

            {/* FORM */}
            <form onSubmit={handleSubmit} className="login-form">
              <Input
                label="Email"
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />

              <Input
                label="Password"
                type="password"
                name="password"
                id="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
              />
              <div className="login-button">
                <Button2
                  name="submit"
                  text="Sign In"
                  type="button"
                  classname="submit-btn"
                />
              </div>
            </form>

            {/* LINKS */}
            <div className="login-footer">
              <Link to="/forgot-password">Forgot password?</Link>
              <span></span>
              <Link to="/register">Create account</Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
